/**
 * The composer.
 *
 * `docs/next.md` called compose and send "the largest single piece left in the
 * product". The server side of it now exists — a MIME builder, an SMTP client,
 * `APPEND` into Sent, and an outbox with a hold — and this is the surface.
 *
 * PLAIN TEXT, AND THAT IS A DECISION RATHER THAN A STAGE ONE. `settings.md` has
 * `/writing/compose_format` with a `rich` default, and a rich editor is a
 * sanitiser, a paste handler, an image path and a serialiser — four surfaces
 * that the reading side spent a whole section learning to distrust. The plain
 * composer is the one that can be finished and trusted; the rich one arrives
 * with the attachment staging endpoint, which it needs anyway.
 *
 * ATTACHMENTS GO UP BEFORE THE MESSAGE IS BUILT, which is why they are uploaded
 * on selection rather than on send: the origin builds the message once and
 * stores it built, so the bytes have to exist first. Each file gets a staging id
 * and the send names them. Removing one before sending deletes the staged copy,
 * so a changed mind costs nothing.
 *
 * ## What the design asks for that IS here
 *
 * The recipient field completes from `/suggest` — an endpoint that has been
 * built, tested and called by nothing since it shipped. Reply and reply-all
 * carry `In-Reply-To` and the full `References` chain, in order, because
 * threading depends on it. And sending does not say "sent": it says the message
 * will go in N seconds, with the Undo the origin's hold makes real.
 *
 * ## A FORWARD CARRIES THE MESSAGE, not a summary of it
 *
 * This is the one compose mode whose content comes from a message that already
 * exists, and it used to drop both halves of it: `setAttachments([])` for every
 * mode including this one, and a body built from `message.text`, which is only
 * the part above the quote. So forwarding a scanned invoice sent a covering
 * note with no invoice, and forwarding a reply sent its newest paragraph with
 * the conversation it was about removed — both silently, both looking complete
 * on screen. The parts are re-staged through `api.restagePart` and the whole
 * body, quote and all, goes in.
 *
 * ## A STAGED FILE BELONGS TO A DRAFT
 *
 * That is the one rule, and everything about attachment lifetime falls out of
 * it. Uploading happens on selection, the ceiling is 25 MB PER ACCOUNT, and the
 * origin's only reclaim is a sweeper that runs a day later — so a composer that
 * closed without saying anything left its uploads holding the budget with
 * nothing anywhere able to list them. Now: Discard releases them, a dismissed
 * composer keeps them and keeps the draft that names them, and opening a
 * composer reclaims anything no draft claims.
 */
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { api, HearthError, type MessageBody, type MessageRow, type Suggestion } from './api.ts'
import { hasMarkup, renderMarkup } from '@hearth/core/mime'
import { selectIdentity } from '@hearth/core/identity'
import { displayName, normaliseAddress, parseAddressList } from '@hearth/core/addresses'

/**
 * What the toolbar inserts.
 *
 * MARKERS AND NOT STYLES. Each of these wraps or prefixes the selection with
 * characters that read correctly in a message nothing renders — which is the
 * whole reason the composer works this way round, and why `*bold*` rather than
 * a `<b>` a browser would have to be asked for.
 */
interface Marker {
  readonly label: string
  readonly title: string
  /** Wrapped around the selection. */
  readonly wrap?: string
  /** Put at the start of each selected line. */
  readonly prefix?: string
  /** Inserted whole, with `$1` standing for the selection. */
  readonly template?: string
}

const MARKERS: readonly Marker[] = [
  { label: 'B', title: 'Bold — *like this*', wrap: '*' },
  { label: 'I', title: 'Italic — _like this_', wrap: '_' },
  { label: '•', title: 'Bullet list', prefix: '- ' },
  { label: '1.', title: 'Numbered list', prefix: '1. ' },
  { label: '❝', title: 'Quote', prefix: '> ' },
  { label: '🔗', title: 'Link — [text](https://…)', template: '[$1](https://)' },
]

/**
 * Apply a marker to the textarea's current selection.
 *
 * IT WORKS ON THE TEXT AND NOT ON THE DOM, so the result is a new value for a
 * controlled input rather than a mutation React does not know about. When there
 * is no textarea to read a selection from — the preview is showing — it appends,
 * which is the least surprising thing a button can do with no cursor.
 */
export function applyMarker(area: HTMLTextAreaElement | null, text: string, marker: Marker): string {
  const start = area?.selectionStart ?? text.length
  const end = area?.selectionEnd ?? text.length
  const selected = text.slice(start, end)
  const before = text.slice(0, start)
  const after = text.slice(end)

  if (marker.template) return before + marker.template.replace('$1', selected || 'text') + after
  if (marker.wrap) {
    const body = selected || 'text'
    return before + marker.wrap + body + marker.wrap + after
  }
  // A PREFIX GOES ON EVERY SELECTED LINE, and on the line the cursor is in when
  // nothing is selected — which is what makes the list buttons useful on a
  // paragraph somebody has already typed.
  const lineStart = before.lastIndexOf('\n') + 1
  const head = text.slice(0, lineStart)
  const block = text.slice(lineStart, end) || ''
  const tail = text.slice(end)
  const prefixed = block
    .split('\n')
    .map((line, index) =>
      marker.prefix === '1. ' ? `${index + 1}. ${line}` : `${marker.prefix}${line}`,
    )
    .join('\n')
  return head + prefixed + tail
}

export type ComposeMode = 'new' | 'reply' | 'reply-all' | 'forward'

export interface ComposeDraft {
  readonly mode: ComposeMode
  /** The message being answered, for a reply or a forward. */
  readonly inReplyTo?: MessageRow
  /** Its body, when the reading pane already has it. */
  readonly quoted?: string
}

export interface ComposeProps {
  readonly draft: ComposeDraft | null
  readonly onClose: () => void
  /** Called with the queued id and the hold, so the shell can offer Undo. */
  readonly onQueued: (queued: { id: number; sendAfter: number; undoSeconds: number }) => void
  readonly onError: (message: string) => void
  /**
   * The account. Used for two things that both have to agree with the rest of
   * the product: a reply does not answer itself, and a stored draft is keyed on
   * the ACCOUNT rather than on anything typed into a field.
   */
  readonly me?: string
}

/** One file the origin is holding for this message. */
export interface StagedFile {
  readonly id: number
  readonly filename: string
  readonly size: number
}

/** The three address fields, as one union that four places switch on. */
type RecipientField = 'to' | 'cc' | 'bcc'

const RECIPIENT_FIELDS: readonly RecipientField[] = ['to', 'cc', 'bcc']
const RECIPIENT_LABELS: Readonly<Record<RecipientField, string>> = { to: 'To', cc: 'Cc', bcc: 'Bcc' }

// ---------------------------------------------------------------------------
// A draft that survives the panel closing.
// ---------------------------------------------------------------------------

/**
 * WHERE AN UNSENT MESSAGE IS KEPT, AND WHAT THAT COSTS.
 *
 * localStorage, in this browser, and NOT the origin. The cost is worth stating
 * plainly rather than discovering: a draft written on the laptop is not on the
 * phone, and clearing site data loses it. What buys that is the alternative.
 * There is no draft route to write to — `/send` builds and queues, and nothing
 * appends to an IMAP `\Drafts` — and the one account-scoped document that does
 * exist is `/settings`, which this repo's memory records as deliberately NOT a
 * cache: every read is a request. Parking a message body there would mean a PUT
 * every half second against a document that is not for bodies. A real Drafts
 * folder is the answer that crosses devices, and it is a larger piece than
 * this one: an append, a supersede, and an edit-and-resend path.
 *
 * KEYED ON THE ACCOUNT AND NEVER ON A TYPED ADDRESS. Two family members share a
 * browser profile in this household, and a single global key would restore one
 * person's unsent message into the other's composer. Account is what every
 * guard in this product compares, and it is what this compares.
 *
 * THE TEXT IS STORED AND THE HTML IS NOT. The composer's whole shape is that
 * the plain text is the source and the rich half is generated from it at send
 * time; storing the generated half would mean parsing it back to edit it, which
 * is the one thing this client refuses to do anywhere.
 */
export interface StoredDraft {
  readonly to: string
  readonly cc: string
  readonly bcc: string
  readonly subject: string
  readonly text: string
  readonly attachments: readonly StagedFile[]
  /** Unix milliseconds. What the eviction order below is on. */
  readonly savedAt: number
}

/**
 * Which draft this is.
 *
 * MODE AND TARGET, because a stored reply may only ever be restored into a
 * reply to the same message. The In-Reply-To and References headers come from
 * `draft.inReplyTo` and not from storage, so restoring a reply's body into a
 * new message would send an answer that no threading algorithm could attach to
 * anything.
 */
export const slotFor = (draft: ComposeDraft): string => `${draft.mode}:${draft.inReplyTo?.id ?? ''}`

const draftKey = (account: string): string => `hearth.drafts.${account}`

/** How many slots one account keeps. Bounded, because a slot per message replied to is not. */
const MAX_SLOTS = 10

/**
 * Every slot this account has — or `null`, meaning the store could not be read
 * at all.
 *
 * NULL IS NOT AN EMPTY SET, and the difference decides whether files get
 * deleted. localStorage throws outright in a private window and in a browser
 * set to block site data; treating that as "no draft claims anything" would let
 * the reclaim below drop the attachments of a draft this code merely could not
 * see.
 */
export function readDrafts(account: string): Record<string, StoredDraft> | null {
  let raw: string | null
  try {
    raw = localStorage.getItem(draftKey(account))
  } catch {
    return null
  }
  if (!raw) return {}
  try {
    const parsed: unknown = JSON.parse(raw)
    if (parsed === null || typeof parsed !== 'object') return {}
    const slots: Record<string, StoredDraft> = {}
    for (const [slot, value] of Object.entries(parsed as Record<string, unknown>)) {
      const stored = asStoredDraft(value)
      if (stored) slots[slot] = stored
    }
    return slots
  } catch {
    // GARBAGE UNDER THE KEY IS NOT AN UNREADABLE STORE, and only the second one
    // is worth failing closed over. Answering null here would turn one bad
    // value into drafts never being saved again — every save reads before it
    // writes — where an empty set lets the next save replace it.
    return {}
  }
}

/**
 * One stored record, shaped the way this component insists on seeing it.
 *
 * localStorage IS INPUT FROM OUTSIDE THE PROGRAM. An older version of this
 * file, another tab, or anybody with a devtools console can have written what
 * is under that key — and a `to` that came back undefined turns a controlled
 * input uncontrolled, while a missing `attachments` array is a crash on `.map`
 * in the restore path. An unreadable draft is dropped; the composer opening
 * empty is a far smaller loss than the composer not opening.
 */
function asStoredDraft(value: unknown): StoredDraft | null {
  if (value === null || typeof value !== 'object') return null
  const stored = value as Partial<StoredDraft>
  const text = (field: unknown): string => (typeof field === 'string' ? field : '')
  return {
    to: text(stored.to),
    cc: text(stored.cc),
    bcc: text(stored.bcc),
    subject: text(stored.subject),
    text: text(stored.text),
    attachments: (Array.isArray(stored.attachments) ? stored.attachments : []).filter(
      (file: unknown): file is StagedFile =>
        file !== null &&
        typeof file === 'object' &&
        typeof (file as StagedFile).id === 'number' &&
        typeof (file as StagedFile).filename === 'string',
    ),
    savedAt: typeof stored.savedAt === 'number' ? stored.savedAt : 0,
  }
}

function writeDrafts(account: string, slots: Record<string, StoredDraft>): void {
  try {
    localStorage.setItem(draftKey(account), JSON.stringify(slots))
  } catch {
    // A full or unavailable store is not worth interrupting anybody for: the
    // message is still on screen and still sendable.
  }
}

export function saveDraft(account: string, slot: string, draft: StoredDraft): void {
  const slots = readDrafts(account)
  if (slots === null) return
  slots[slot] = draft
  // OLDEST OUT FIRST. Ten is more unsent messages at once than this household
  // has ever had, and unbounded growth in a store with a quota ends as a write
  // that throws and a draft that is silently not saved.
  const oldest = Object.keys(slots).sort((a, b) => (slots[a]?.savedAt ?? 0) - (slots[b]?.savedAt ?? 0))
  for (const key of oldest.slice(0, Math.max(0, oldest.length - MAX_SLOTS))) delete slots[key]
  writeDrafts(account, slots)
}

export function clearDraft(account: string, slot: string): void {
  const slots = readDrafts(account)
  if (slots === null || !(slot in slots)) return
  delete slots[slot]
  writeDrafts(account, slots)
}

/** Every staged id any stored draft claims, so the reclaim knows what is spoken for. */
const claimedIds = (slots: Record<string, StoredDraft>): Set<number> => {
  const claimed = new Set<number>()
  for (const stored of Object.values(slots)) {
    for (const file of stored.attachments) claimed.add(file.id)
  }
  return claimed
}

/**
 * How old an unclaimed staged file must be before the composer deletes it.
 *
 * NOT ZERO, AND THE REASON IS ANOTHER TAB. The staged list is per ACCOUNT, so a
 * second composer open in a second tab has files in it that this one's storage
 * knows nothing about — a zero-age reclaim would delete a file somebody is
 * looking at. An hour is far longer than any upload and far shorter than the
 * origin's day. A file older than that, claimed by no draft, is a leak.
 */
const RECLAIM_AFTER_S = 60 * 60

/** How long after the last keystroke a draft is written. */
const SAVE_AFTER_MS = 500

/**
 * An address list — a header the origin sent, or a field somebody typed — as
 * recipients.
 *
 * CORE'S SCANNER, NOT A COMMA SPLIT, and the difference is a reply that cannot
 * be sent at all. `raw.split(',')` turns `Weber, Anna <anna@example.com>` into
 * `Weber` and `Anna <anna@example.com>`; the first has no `@`, so `/send`
 * refuses the whole message with 400 `"Weber" is not an address` and the reader
 * has to hand-edit a field the composer filled in for them. That shape is not
 * exotic — `packages/core/src/addresses/parse.ts` names THE UNQUOTED COMMA in
 * its own comment as the reason it is a scanner rather than a regex, and calls
 * it "the shape Exchange and Outlook emit constantly". This client already
 * imports that scanner for the sender column; there is no second opinion about
 * address syntax to have.
 *
 * `ParsedAddress.name` IS STILL RFC 2047 ENCODED where parse.ts declares it,
 * because encoded words must be decoded AFTER the split or one containing a
 * comma splits the list. `displayName` is that decode, and it is the same call
 * `format.ts` makes for the same reason. Passing the raw form to `/send` would
 * hand the origin's MIME builder `=?utf-8?B?…?=` to encode a second time.
 */
const parseRecipients = (raw: string | null | undefined): { address: string; name?: string }[] =>
  parseAddressList(raw).map((parsed) => {
    const name = displayName(parsed.name)
    return name ? { address: parsed.address, name } : { address: parsed.address }
  })

/**
 * Who an address IS, for the two comparisons a reply-all has to make: "is this
 * the reader" and "is this somebody already on a line above".
 *
 * ONE FUNCTION FOR BOTH, because they have to agree. An exclusion that folds
 * case on one side and not the other copies the reader to themselves in exactly
 * the headers that spell their address differently, which is the failure the
 * self-exclusion exists to prevent.
 *
 * `normaliseAddress` is the product's existing answer and this reads it rather
 * than restating it: it un-angles, lowercases with `toLowerCase` and never the
 * locale form, and strips plus-detail. THE `base` AND NOT THE `addr`, because
 * `dh+masons@h-group.net` on a Cc: is still the reader — `identity/index.ts`
 * has a whole `plus-detail` rule saying so — and a reply-all that copies the
 * reader on their own reply because the list wrote to an alias is the same bug
 * with an extra step.
 */
const identityOf = (address: string): string => normaliseAddress(address).base

/**
 * The fragment after the last comma: what the reader is typing right now.
 *
 * NOT AN ADDRESS PARSER AND NOT USED AS ONE. Two places need "the bit at the
 * end of the field" — completion, which probes the origin with it, and the
 * refusal below, which asks whether it ever became a recipient — and they have
 * to point at the same characters or the composer completes one fragment and
 * complains about another.
 */
const lastFragment = (raw: string): string => raw.split(',').pop()?.trim() ?? ''

/**
 * The tail of a field that never became a recipient, or null.
 *
 * IT KEEPS A PROPERTY THE OLD COMMA SPLIT HAD BY ACCIDENT. `parseAddressList`
 * discards a fragment only when it holds no `@` and no angle brackets, because
 * it reads that as the front half of a display name and puts it back in front
 * of the next address — the recovery `Weber, Anna <…>` depends on. In a HEADER
 * that is right. In a field somebody just typed, `alice@example.com, bo` means
 * two people, and a trailing fragment with no address after it to attach to is
 * dropped on the floor: the old split handed `bo` to the origin and got back
 * 400 `"bo" is not an address`, so swapping the parser in without this would
 * have traded a refusal for a message quietly going to fewer people than the
 * reader addressed it to. That is the same silent narrowing as the Cc defect
 * this change exists to fix, and introducing it here would be a poor trade.
 *
 * EVERY FRAGMENT, NOT ONLY THE TAIL, and the first version checked only the
 * tail on the reasoning that anything earlier "reaches the origin either way".
 * That is true of the CHARACTERS and false of the RECIPIENT, which is the thing
 * being counted: `parseAddressList('bo, alice@example.com')` returns ONE
 * address — `alice@example.com` with the display name `bo` — so `bo` does not
 * merely fail to be a recipient, the mail goes to Alice wearing Bo's name. And
 * a trailing comma, which is exactly what accepting a completion suggestion
 * leaves behind, made the tail empty and switched the whole guard off.
 *
 * SO IT COUNTS: however many separators somebody typed, that is how many people
 * they meant, and if the parser found fewer then one of them is not an address.
 * The refusal names the first fragment that did not survive.
 *
 * IT REFUSES ONE THING A HEADER ACCEPTS, deliberately: an UNQUOTED
 * `Weber, Anna <anna@example.com>` pasted out of Outlook is two typed fragments
 * and one parsed address, so it is refused with "Weber is not an address". The
 * quoted form works, and so does deleting the comma. That is a visible refusal
 * one edit from correct, against the alternative — mail going to Alice under
 * Bo's name, silently — and this repo has spent a long day on the difference.
 */
/**
 * The commas somebody meant as separators: not the ones inside a quoted name or
 * an angle-bracketed address.
 *
 * A DIFFERENT QUESTION FROM THE PARSER'S, and that is why it is here rather
 * than borrowed. `parseAddressList` answers "what addresses does this HEADER
 * carry", and its comment is explicit that `Weber, Anna <anna@x>` and
 * `Undisclosed, alice@x` cannot be told apart — of the two readings it picks the
 * surname, which is right for a header. This asks "how many recipients did a
 * person just TYPE into a field", where the answer is the number of separators
 * they used, and a naive `split(',')` gets it wrong for `"Weber, Anna" <…>`.
 */
function typedFragments(raw: string): string[] {
  const out: string[] = []
  let current = ''
  let quoted = false
  let angled = false
  for (let i = 0; i < raw.length; i++) {
    const ch = raw[i]!
    if (ch === '\\' && quoted) {
      current += ch + (raw[i + 1] ?? '')
      i++
      continue
    }
    if (ch === '"') quoted = !quoted
    else if (ch === '<' && !quoted) angled = true
    else if (ch === '>' && !quoted) angled = false
    if (ch === ',' && !quoted && !angled) {
      out.push(current.trim())
      current = ''
      continue
    }
    current += ch
  }
  out.push(current.trim())
  return out.filter((fragment) => fragment !== '')
}

const unaddressed = (raw: string): string | null => {
  if (raw.trim() === '') return null
  const typed = typedFragments(raw)
  if (typed.length === 0) return null
  if (parseRecipients(raw).length >= typed.length) return null
  return typed.find((fragment) => parseRecipients(fragment).length === 0) ?? typed[typed.length - 1]!
}

/**
 * `Re:` once, however many times it has been replied to.
 *
 * A subject that grows `Re: Re: Re:` is the oldest and most visible sign of a
 * client that treats a prefix as text. The pattern covers the localised forms
 * this household's mail actually carries.
 */
export function replySubject(subject: string | null): string {
  const stripped = (subject ?? '').replace(/^\s*((re|aw|antw|sv|vs|odp)\s*(\[\d+\])?\s*:\s*)+/i, '')
  return `Re: ${stripped}`
}

export function forwardSubject(subject: string | null): string {
  const stripped = (subject ?? '').replace(/^\s*((fwd?|wg|tr)\s*:\s*)+/i, '')
  return `Fwd: ${stripped}`
}

/**
 * The quoted body of a reply.
 *
 * `>`-prefixed, with an attribution line, which is what every client produces
 * and what `mime/quote.ts`'s splitter on the READING side knows how to fold
 * back up again. The two are deliberately the same convention: a reply Hearth
 * writes is one Hearth can later collapse.
 */
export function quoteBody(from: string | null, date: number, body: string): string {
  const when = new Date(date * 1000).toLocaleString()
  const attribution = `On ${when}, ${from ?? 'they'} wrote:`
  const quoted = body
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map((line) => (line.startsWith('>') ? `>${line}` : `> ${line}`))
    .join('\n')
  return `\n\n${attribution}\n${quoted}\n`
}

/**
 * The WHOLE body of a message, quote and all.
 *
 * `/body` splits what a sender wrote from the conversation underneath it and
 * the reading pane folds the second half away. A REPLY may drop that tail: the
 * person being answered already has it. A FORWARD may not — the tail IS the
 * thing being forwarded, and dropping it hands somebody the newest paragraph of
 * a conversation they have never seen any of.
 */
export const wholeBody = (body: Pick<MessageBody, 'text' | 'quoted'> | null): string =>
  body === null ? '' : body.quoted ? `${body.text}\n${body.quoted}` : body.text

/** The header block a forward puts above the message it is carrying. */
export function forwardBody(row: MessageRow, body: string): string {
  return (
    `\n\n---------- Forwarded message ----------\nFrom: ${row.from_addr_name ?? ''}\n` +
    `Subject: ${row.subject ?? ''}\nTo: ${row.to_addrs ?? ''}\n\n${body}`
  )
}

/**
 * Everyone a reply-all goes to, minus the account itself.
 *
 * BOTH RECIPIENT HEADERS, WHICH IS THE ENTIRE POINT OF THE FUNCTION. `To` is
 * whoever wrote the message; `Cc` is everybody else who was already on the
 * conversation — the original's To: line AND its Cc: line. Building Cc out of
 * `to_addrs` alone, which was all the wire carried, dropped every person who
 * was on Cc and demoted every person who was on To: a four-person thread
 * answered as a private reply to one, silently, on the most ordinary action in
 * a mail client. `MessageRow.cc_addrs` exists for this line and nothing else.
 */
export function replyAllRecipients(
  row: MessageRow,
  me: string | undefined,
): { to: string; cc: string } {
  // ONE SET DOES BOTH EXCLUSIONS, seeded with the reader. "Not me" and "not
  // already on a line above" are then the same test rather than two that can
  // disagree, and because it GROWS as the lines are built, somebody who was on
  // the original's To: and its Cc: — which Outlook does routinely, and which
  // reading two headers instead of one has now made reachable — is copied once.
  //
  // An unknown account seeds nothing, so the worst a missing `me` can do is
  // copy the reader to themselves. It can never drop a real recipient.
  const seen = new Set<string>(me ? [identityOf(me)] : [])
  const take = (raw: string | null): string[] => {
    const kept: string[] = []
    for (const { address } of parseRecipients(raw)) {
      const who = identityOf(address)
      if (who === '' || seen.has(who)) continue
      seen.add(who)
      kept.push(address)
    }
    return kept
  }
  const to = take(row.from_addr_name)
  // TO IS TAKEN FIRST AND THE ORDER IS LOAD-BEARING: whoever is answered goes
  // on the To: line, and the same person appearing again in the original's
  // recipients must fall out of Cc rather than the other way round.
  const cc = [...take(row.to_addrs), ...take(row.cc_addrs)]
  return { to: to.join(', '), cc: cc.join(', ') }
}

export function Compose({ draft, onClose, onQueued, onError, me }: ComposeProps): React.JSX.Element | null {
  const ids = useId()
  const panelRef = useRef<HTMLDivElement>(null)
  const returnTo = useRef<HTMLElement | null>(null)

  const [to, setTo] = useState('')
  const [cc, setCc] = useState('')
  const [bcc, setBcc] = useState('')
  const [subject, setSubject] = useState('')
  const [text, setText] = useState('')
  const [previewing, setPreviewing] = useState(false)
  const bodyRef = useRef<HTMLTextAreaElement>(null)
  const [sending, setSending] = useState(false)
  const [suggestions, setSuggestions] = useState<readonly Suggestion[]>([])
  const [suggestFor, setSuggestFor] = useState<RecipientField | null>(null)
  const [attachments, setAttachments] = useState<readonly StagedFile[]>([])
  const [uploading, setUploading] = useState(0)
  /**
   * The disposable addresses this account has minted, for the one question they
   * exist to answer: which address should this reply go out as.
   *
   * WITHOUT THIS A REPLY UNDOES THE ADDRESS. Somebody hands
   * `m.7fq2.petco.k3n9wq4x@m.example.com` to a shop precisely so the shop never
   * learns the household's real address — and then answers the shop's mail from
   * `dh@h-group.net`, which is the one thing the disposable address was for.
   * The panel that mints them says as much in so many words: "replies go
   * straight from the address itself".
   *
   * READ WHEN THE COMPOSER OPENS, not on mount. Most sessions never compose, the
   * list is small, and a copy cached from an hour ago would miss the address
   * minted five minutes ago at a till — which is exactly the one being replied
   * from.
   *
   * A FAILURE LEAVES IT EMPTY AND SENDS AS THE ACCOUNT. That is the answer this
   * client gave for its whole life so far, so a dead origin costs a nicety
   * rather than a message.
   */
  const [mine, setMine] = useState<readonly string[]>([])

  /**
   * ONE TABLE AND NOT THREE TERNARIES.
   *
   * `'to' | 'cc'` became `'to' | 'cc' | 'bcc'` in four places at once, and
   * widening three of them compiles perfectly while writing every Bcc
   * suggestion into Cc. A record cannot be widened halfway — the type demands
   * all three entries, and every reader indexes it rather than restating the
   * branch.
   */
  const fields: Readonly<Record<RecipientField, { value: string; set: (value: string) => void }>> = {
    to: { value: to, set: setTo },
    cc: { value: cc, set: setCc },
    bcc: { value: bcc, set: setBcc },
  }

  /** The message being answered or forwarded, which is where threading comes from. */
  const source = draft?.inReplyTo ?? null

  useEffect(() => {
    if (!draft) return
    let live = true
    api
      .addresses()
      .then((book) => {
        if (!live) return
        setMine((book.masked ?? []).map((one) => one.source).filter((one): one is string => typeof one === 'string'))
      })
      .catch(() => undefined)
    return () => {
      live = false
    }
  }, [draft])

  /**
   * Which of this account's addresses this message goes out as.
   *
   * `selectIdentity` IS `packages/core`'s AND HAD NO CALLER. It reads
   * X-Original-To, then Delivered-To, then To:, then Cc: — the order matters
   * because the first two are the envelope recipient and the visible headers are
   * only evidence about it. This client has the visible headers, which is what
   * an ordinary signup confirmation carries: the shop wrote to the address it
   * was given, so it is in To:.
   *
   * THE ACCOUNT IS ALWAYS IN THE LIST AND IS ALWAYS THE FALLBACK. `selectIdentity`
   * answers null when nothing matches, and null means "as yourself" — which is
   * what every message before this one did.
   *
   * A NEW MESSAGE IS ALWAYS FROM THE ACCOUNT. There is no incoming message to
   * read, and guessing a disposable address for a message nobody replied to
   * would be handing a shop's address to somebody else.
   */
  const sendAs = ((): string => {
    if (!me || !source || draft?.mode === 'new') return me ?? ''
    const identities = [
      { id: 'account', address: me, isDefault: true },
      ...mine.map((address) => ({ id: address, address })),
    ]
    const chosen = selectIdentity(identities, {
      // FIRST, AND THE ORDER IS `selectIdentity`'S. `X-Original-To` is the
      // envelope recipient before alias rewriting, so it names the disposable
      // address itself; To: and Cc: are evidence about it and are what an
      // ordinary signup confirmation carries. Mail that arrived by Bcc or
      // through a list has only the first.
      ...(source.original_to ? { originalTo: source.original_to } : {}),
      to: parseAddressList(source.to_addrs ?? '').map((one) => one.address),
      cc: parseAddressList(source.cc_addrs ?? '').map((one) => one.address),
    })
    return chosen?.identity.address ?? me
  })()

  /**
   * `onError`, reachable from an effect without being one of its dependencies.
   *
   * THE SHELL PASSES A STABLE `setNotice`, and nothing may depend on it doing
   * so. An effect below asks the origin what it is holding, once per open; with
   * a parent that inlined its handler that same effect would fire on every
   * render, which is a request per keystroke.
   */
  const notify = useRef(onError)
  useEffect(() => {
    notify.current = onError
  }, [onError])

  /** The draft on screen, readable from a callback that outlives it. */
  const showing = useRef<ComposeDraft | null>(null)
  /** Whether there is anything here it would be a loss to throw away. */
  const edited = useRef(false)
  /** The slot a restored draft came from, or null when this composer was prefilled. */
  const restoredFrom = useRef<string | null>(null)
  /** The staged ids a restore brought back. Only these are worth reconciling. */
  const restoredIds = useRef<readonly number[]>([])
  /** The last body the prefill itself wrote. See `applyBody`. */
  const prefilled = useRef<string | null>(null)
  /** The draft whose forwarded parts have already been carried. */
  const carriedFor = useRef<ComposeDraft | null>(null)

  /**
   * Put a prefilled body in the box — unless the reader is already using it.
   *
   * THE QUOTE ARRIVES A SECOND AFTER THE PANEL DOES, because it is a fetch. The
   * old code replaced the body every time that resolved, so a reply somebody
   * started typing immediately lost its first sentence a moment later: a
   * data-loss bug wearing the costume of a convenience. This only ever replaces
   * text the prefill itself last wrote.
   */
  const applyBody = (body: string): void => {
    // READ THE REF BEFORE MOVING IT, and pass the old value INTO the updater.
    // React runs an updater at render time, not at the call, so an updater that
    // read `prefilled.current` itself would read the value this line had
    // already written — the comparison would always fail and the prefill would
    // never land at all.
    const previous = prefilled.current
    prefilled.current = body
    setText((current) => (previous === null || current === previous ? body : current))
  }

  /**
   * The body of the message being answered or forwarded, fetched when the
   * caller did not bring one.
   *
   * A REPLY WITH NO QUOTE IS STILL A REPLY, so this never blocks the composer:
   * the fields fill in immediately and the quoted text arrives after. A failed
   * fetch leaves the quote out rather than the message unwritable — the reader
   * has something to say either way.
   *
   * A FORWARD FETCHES EVEN WHEN THE CALLER BROUGHT A BODY, because the parts
   * list arrives with it and `draft.quoted` is only the un-quoted prefix. Both
   * halves of what a forward has to carry are in this response and in nothing
   * else the client holds.
   */
  const [sourceBody, setSourceBody] = useState<MessageBody | null>(null)
  useEffect(() => {
    setSourceBody(null)
    if (!draft || draft.mode === 'new' || !draft.inReplyTo) return
    if (draft.quoted !== undefined && draft.mode !== 'forward') return
    let live = true
    api
      .body(draft.inReplyTo.id)
      .then((message) => {
        if (live) setSourceBody(message)
      })
      .catch(() => undefined)
    return () => {
      live = false
    }
  }, [draft])

  /**
   * Opening the panel: restore what was stored for this exact draft, or prefill.
   *
   * ONE OR THE OTHER, NEVER BOTH. A restored draft is what somebody wrote, and
   * the prefill is what the composer would have written for them — so a restore
   * turns the prefill off entirely, including the forward's part-carrying
   * below, which the stored draft already names the results of.
   */
  useEffect(() => {
    showing.current = draft
    if (!draft) return
    edited.current = false
    restoredFrom.current = null
    restoredIds.current = []
    prefilled.current = null
    setAttachments([])

    const slot = slotFor(draft)
    const stored = me ? (readDrafts(me)?.[slot] ?? null) : null
    if (stored) {
      restoredFrom.current = slot
      restoredIds.current = stored.attachments.map((file) => file.id)
      setTo(stored.to)
      setCc(stored.cc)
      setBcc(stored.bcc)
      setSubject(stored.subject)
      setText(stored.text)
      setAttachments(stored.attachments)
      // A RESTORED DRAFT KEEPS BEING SAVED. Restoring one and then dismissing
      // the panel without typing must not be what finally loses it.
      edited.current = true
      return
    }

    setBcc('')
    if (draft.mode === 'new') {
      setTo('')
      setCc('')
      setSubject('')
      applyBody('')
      return
    }
    const row = draft.inReplyTo
    if (!row) return
    if (draft.mode === 'forward') {
      setTo('')
      setCc('')
      setSubject(forwardSubject(row.subject))
      applyBody(forwardBody(row, draft.quoted ?? ''))
      return
    }
    const all = replyAllRecipients(row, me)
    setTo(draft.mode === 'reply-all' ? all.to : (parseRecipients(row.from_addr_name)[0]?.address ?? ''))
    setCc(draft.mode === 'reply-all' ? all.cc : '')
    setSubject(replySubject(row.subject))
    applyBody(quoteBody(row.from_addr_name, row.date, draft.quoted ?? ''))
  }, [draft, me])

  /** The quoted body, once it has arrived. */
  useEffect(() => {
    if (!draft || !sourceBody || restoredFrom.current !== null) return
    const row = draft.inReplyTo
    if (!row) return
    applyBody(
      draft.mode === 'forward'
        ? forwardBody(row, wholeBody(sourceBody))
        : quoteBody(row.from_addr_name, row.date, sourceBody.text),
    )
  }, [draft, sourceBody])

  /**
   * Carry a forward's files, by staging each part as an upload of its own.
   *
   * ONCE PER DRAFT, which the ref enforces: React's StrictMode runs an effect
   * twice in development, and `api.body` is a GET that does not care while
   * staging twice would attach every file twice — and charge the account's
   * 25 MB budget for both copies.
   *
   * A FAILURE IS LOUD. The whole defect this replaces was silence: a composer
   * that showed no chips and sent no files while looking finished. If a part
   * cannot be carried, the reader is told which one, before they send.
   */
  useEffect(() => {
    if (!draft || draft.mode !== 'forward' || !source || !sourceBody) return
    if (restoredFrom.current !== null || carriedFor.current === draft) return
    carriedFor.current = draft
    void (async () => {
      // EVERYTHING THE READING PANE CALLS AN ATTACHMENT, inline parts included,
      // because it is the same list: what somebody saw listed under the message
      // is what they expect to arrive with the forward. Filtering `inline` here
      // would be a second opinion about what an attachment is, and this repo's
      // most common defect is two predicates about one thing.
      for (const part of sourceBody.attachments) {
        // The origin insists on a name and some parts have none; the part
        // number is at least unique within the message.
        const filename = part.filename ?? `part-${part.part}`
        setUploading((count) => count + 1)
        try {
          const staged = await api.restagePart(source.id, part.part, filename, part.type)
          if (showing.current === draft) {
            setAttachments((current) => [...current, staged])
            edited.current = true
          } else {
            // The panel closed while this was in flight. Nobody will ever name
            // this id in a send, so it is a leak from the moment it lands.
            void api.dropAttachment(staged.id).catch(() => undefined)
          }
        } catch (error) {
          notify.current(
            error instanceof HearthError
              ? `${filename} could not be forwarded: ${error.message}`
              : `${filename} could not be forwarded`,
          )
        } finally {
          setUploading((count) => count - 1)
        }
      }
    })()
  }, [draft, source, sourceBody])

  /**
   * Reconcile the composer with what the origin is actually holding, and
   * reclaim what nothing claims.
   *
   * TWO JOBS AND ONE REQUEST, because they are two halves of the same
   * question. A restored draft names staged ids that may be a day old and swept
   * — chips for files that are gone are a message that will be refused at send
   * time, so they come off and the reader is told. And a staged file no stored
   * draft names, old enough that no composer anywhere is still uploading it, is
   * a leak: nothing else in this product has ever been able to see one, and it
   * holds part of a 25 MB per-account budget for a day behind an error message
   * that blames the message in hand.
   *
   * IT FAILS CLOSED, and does so BEFORE it asks. Both halves are questions
   * about what drafts claim, so a store that cannot be read — a private window,
   * a browser blocking site data — means nothing was restored to reconcile and
   * nothing can be shown to be unclaimed. There is no request worth making, let
   * alone a deletion worth doing. A request that then fails leaves both halves
   * undone rather than guessing.
   */
  useEffect(() => {
    if (!draft || !me) return
    const slots = readDrafts(me)
    if (slots === null) return
    let live = true
    void api
      .stagedAttachments()
      .then(({ attachments: staged }) => {
        if (!live || !Array.isArray(staged)) return
        const held = new Set(staged.map((part) => part.id))
        const missing = restoredIds.current.filter((id) => !held.has(id))
        if (missing.length > 0) {
          setAttachments((current) => current.filter((file) => !missing.includes(file.id)))
          notify.current(
            missing.length === 1
              ? 'One file on this draft is no longer held by the server and has been removed'
              : `${missing.length} files on this draft are no longer held by the server and have been removed`,
          )
        }
        const claimed = claimedIds(slots)
        const now = Date.now() / 1000
        for (const part of staged) {
          if (claimed.has(part.id) || now - part.createdAt <= RECLAIM_AFTER_S) continue
          void api.dropAttachment(part.id).catch(() => undefined)
        }
      })
      .catch(() => undefined)
    return () => {
      live = false
    }
  }, [draft, me])

  /** This composer as a stored draft. */
  const record = (): StoredDraft => ({ to, cc, bcc, subject, text, attachments, savedAt: Date.now() })

  /**
   * Save what is here, a moment after the typing stops.
   *
   * ONLY WHAT SOMEBODY DID. `edited` is false through the whole prefill — the
   * reset, the recipients, the quote arriving a second later — so opening a
   * reply and closing it again writes nothing, and the slot a real draft is in
   * is not overwritten by a composer nobody typed in.
   */
  useEffect(() => {
    if (!draft || !me || !edited.current) return
    const slot = slotFor(draft)
    const stored = record()
    const timer = setTimeout(() => saveDraft(me, slot, stored), SAVE_AFTER_MS)
    return () => clearTimeout(timer)
  }, [draft, me, to, cc, bcc, subject, text, attachments])

  /**
   * Put the panel away, keeping the message.
   *
   * `interaction.md` calls Escape a minimise rather than a close, and this is
   * what makes that true across a panel that really is unmounted: the draft is
   * written SYNCHRONOUSLY here, because the debounced save above is cancelled
   * by its own cleanup on the way out and would otherwise lose the last half
   * second of typing.
   *
   * THE STAGED FILES STAY, and that is the point rather than an oversight. They
   * are named by the draft that was just written; releasing them here would
   * turn "put it aside" into "lose the invoice you attached to it". Discard is
   * what releases, and the reclaim above is what catches everything else.
   */
  const dismiss = useCallback(() => {
    if (draft && me && edited.current) saveDraft(me, slotFor(draft), record())
    onClose()
  }, [draft, me, to, cc, bcc, subject, text, attachments, onClose])

  /**
   * Throw the message away, and the bytes with it.
   *
   * THE ONE PLACE A WHOLE COMPOSER'S UPLOADS ARE RELEASED. Before this, every
   * way out of the panel dropped the ids and left the files: a 20 MB video
   * attached and thought better of cost the account 20 MB of its 25 MB ceiling
   * for the next day, and the error it produced blamed the next message somebody
   * tried to attach anything to.
   */
  const discard = useCallback(() => {
    if (draft && me) clearDraft(me, slotFor(draft))
    for (const file of attachments) void api.dropAttachment(file.id).catch(() => undefined)
    onClose()
  }, [draft, me, attachments, onClose])

  // Escape puts it away, on the document rather than the panel, for the reason
  // `Settings.tsx` sets out: the panel is not guaranteed to hold focus.
  useEffect(() => {
    if (!draft) return
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') dismiss()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [draft, dismiss])

  useEffect(() => {
    if (!draft) return
    const panel = panelRef.current
    const opener = document.activeElement
    if (opener instanceof HTMLElement && !panel?.contains(opener)) returnTo.current = opener
    // A new message starts in To; a reply starts in the body, because the
    // recipients are already right and the reader has something to say.
    const first = panel?.querySelector<HTMLElement>(
      draft.mode === 'new' ? '[data-compose-first]' : '[data-compose-body]',
    )
    first?.focus()
    return () => returnTo.current?.focus()
  }, [draft])

  /**
   * Address completion, from the endpoint that has existed and been called by
   * nothing since it shipped.
   *
   * DEBOUNCED, because it runs per keystroke against an index of 120,000
   * addresses. Measured at the origin: a `/suggest` probe is single-digit
   * milliseconds, and a request per character is still a request per character.
   */
  const probe = useMemo(() => {
    let timer: ReturnType<typeof setTimeout> | null = null
    return (value: string, field: RecipientField): void => {
      if (timer) clearTimeout(timer)
      const last = lastFragment(value)
      if (last.length < 2) {
        setSuggestions([])
        setSuggestFor(null)
        return
      }
      timer = setTimeout(() => {
        api
          .suggest(last, 6)
          .then((result) => {
            setSuggestions(result.suggestions)
            setSuggestFor(field)
          })
          .catch(() => setSuggestions([]))
      }, 150)
    }
  }, [])

  const accept = (field: RecipientField, suggestion: Suggestion): void => {
    const parts = fields[field].value.split(',')
    parts[parts.length - 1] = ` ${suggestion.name ? `${suggestion.name} <${suggestion.address}>` : suggestion.address}`
    edited.current = true
    fields[field].set(parts.join(',').replace(/^\s+/, '') + ', ')
    setSuggestions([])
    setSuggestFor(null)
  }

  const send = useCallback(async () => {
    if (sending) return
    const recipients = { to: parseRecipients(to), cc: parseRecipients(cc), bcc: parseRecipients(bcc) }
    // BEFORE THE EMPTINESS CHECK, so a field holding only `bo` is refused by
    // NAME rather than by "nobody to go to", which says nothing about which of
    // three fields is wrong.
    const stray = unaddressed(to) ?? unaddressed(cc) ?? unaddressed(bcc)
    if (stray !== null) {
      onError(`${stray} is not an address`)
      return
    }
    // ALL THREE FIELDS, BECAUSE THE ORIGIN COUNTS ALL THREE (`app.ts`: "a
    // message needs at least one recipient", over to + cc + bcc). Counting only
    // `To` here made a Bcc-only message — the main reason anybody wants a Bcc
    // field — unsendable, with an error about having nobody to send it to while
    // the server would have taken it.
    if (recipients.to.length + recipients.cc.length + recipients.bcc.length === 0) {
      onError('A message needs somebody to go to')
      return
    }
    setSending(true)
    try {
      const queued = await api.send({
        // AS THE ADDRESS THE CORRESPONDENT WROTE TO, when that was one of this
        // account's disposable ones. The origin checks it against the addresses
        // this account actually minted; naming any other is a 403.
        ...(sendAs && me && sendAs !== me ? { from: { address: sendAs } } : {}),
        ...(recipients.to.length > 0 ? { to: recipients.to } : {}),
        ...(attachments.length > 0 ? { attachments: attachments.map((file) => file.id) } : {}),
        ...(recipients.cc.length > 0 ? { cc: recipients.cc } : {}),
        ...(recipients.bcc.length > 0 ? { bcc: recipients.bcc } : {}),
        subject,
        text,
        // THE RICH HALF, ONLY WHEN THERE IS ONE. A message with no formatting
        // stays a single `text/plain` part rather than becoming multipart for
        // nothing — and a reader whose client shows the plain half sees exactly
        // what was typed either way.
        ...(hasMarkup(text) ? { html: renderMarkup(text) } : {}),
        ...(source?.message_id ? { inReplyTo: source.message_id } : {}),
        // THE CHAIN, OLDEST FIRST. `threading/` reduces a reference set in
        // References order, and a defect in exactly that fragmented 45% of this
        // archive's conversations before Phase 0.
        ...(source?.thread_id && source.message_id && source.thread_id !== source.message_id
          ? { references: [source.thread_id, source.message_id] }
          : source?.message_id
            ? { references: [source.message_id] }
            : {}),
      })
      onQueued(queued)
      // GONE FROM STORAGE THE MOMENT IT IS QUEUED. A draft cleared on Discard
      // but not on send is a message that comes back from the dead the next
      // time this composer opens, addressed and ready to go a second time. The
      // staged files need no releasing here: the origin drops them itself as it
      // builds, which is why they are not dropped before the build.
      if (draft && me) clearDraft(me, slotFor(draft))
      onClose()
    } catch (error) {
      onError(error instanceof HearthError ? error.message : 'That message could not be queued')
    } finally {
      setSending(false)
    }
  }, [sending, to, cc, bcc, subject, text, source, attachments, draft, me, onQueued, onClose, onError])

  /**
   * Upload on selection, not on send.
   *
   * A photo takes as long as it takes, and doing it when Send is pressed means
   * the reader waits after the moment they thought they were finished. Doing it
   * here means the progress is where the decision was.
   */
  const attach = useCallback(
    async (files: FileList | null): Promise<void> => {
      for (const file of [...(files ?? [])]) {
        setUploading((count) => count + 1)
        try {
          const staged = await api.stageAttachment(file)
          // BYTES SPENT COUNT AS WORK. A composer with a file in it and nothing
          // typed is still worth storing, because the alternative is that the
          // file is held by the account and named by nothing.
          edited.current = true
          setAttachments((current) => [...current, staged])
        } catch (error) {
          onError(
            error instanceof HearthError && error.code === 'too_large'
              ? error.message
              : `${file.name} could not be attached`,
          )
        } finally {
          setUploading((count) => count - 1)
        }
      }
    },
    [onError],
  )

  const detach = useCallback((id: number): void => {
    edited.current = true
    setAttachments((current) => current.filter((file) => file.id !== id))
    // Best effort: the reclaim on the next open catches anything this misses.
    void api.dropAttachment(id).catch(() => undefined)
  }, [])

  if (!draft) return null

  const field = (label: string, id: string, value: string, set: (value: string) => void) => (
    <label className="compose-field" htmlFor={id}>
      <span className="compose-label">{label}</span>
      <input
        id={id}
        className="compose-input"
        value={value}
        autoComplete="off"
        onChange={(event) => {
          edited.current = true
          set(event.target.value)
        }}
      />
    </label>
  )

  return (
    <div
      className="settings-scrim"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) dismiss()
      }}
    >
      <div
        className="settings-panel compose-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${ids}-title`}
        tabIndex={-1}
        ref={panelRef}
      >
        <div className="settings-head">
          <h2 className="settings-title" id={`${ids}-title`}>
            {draft.mode === 'forward' ? 'Forward' : draft.mode === 'new' ? 'New message' : 'Reply'}
          </h2>
          <button className="settings-close" onClick={dismiss} aria-label="Close">
            ×
          </button>
        </div>

        <div className="compose-body">
          {/* WHICH ADDRESS THIS GOES OUT AS, and only when it is not the obvious
              one. A "From: dh@h-group.net" line on every reply is furniture; the
              same line reading `m.7fq2.petco.k3n9wq4x@m.example.com` is the
              single most important thing on the screen, because it is the
              difference between answering a shop as yourself and answering it as
              the address you gave them. Not editable here: the origin decides
              what this account may send as, and a control offering a choice it
              would refuse is worse than none. */}
          {sendAs && me && sendAs !== me ? (
            <p className="compose-sending-as">
              <span className="compose-label">From</span>
              <code>{sendAs}</code>
              <span className="compose-as-why">the address they wrote to</span>
            </p>
          ) : null}
          {/* THREE FIELDS FROM ONE LIST, so a change to what a recipient field
              does cannot reach two of them and miss the third. */}
          {RECIPIENT_FIELDS.map((which) => (
            <label className="compose-field" htmlFor={`${ids}-${which}`} key={which}>
              <span className="compose-label">{RECIPIENT_LABELS[which]}</span>
              <input
                id={`${ids}-${which}`}
                className="compose-input"
                value={fields[which].value}
                autoComplete="off"
                {...(which === 'to' ? { 'data-compose-first': true } : {})}
                onChange={(event) => {
                  edited.current = true
                  fields[which].set(event.target.value)
                  probe(event.target.value, which)
                }}
              />
            </label>
          ))}
          {suggestFor && suggestions.length > 0 && (
            <ul className="compose-suggestions" role="listbox" aria-label="Address suggestions">
              {suggestions.map((suggestion) => (
                <li key={suggestion.address}>
                  <button type="button" onClick={() => accept(suggestFor, suggestion)}>
                    <span className="compose-suggest-name">{suggestion.name || suggestion.address}</span>
                    {suggestion.name && <span className="compose-suggest-address">{suggestion.address}</span>}
                    <span className="compose-suggest-count">{suggestion.messages}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {field('Subject', `${ids}-subject`, subject, setSubject)}

          {/* THE TEXT IS THE SOURCE AND THE HTML IS GENERATED FROM IT — see
              `packages/core/src/mime/markup.ts` for why this way round. The
              toolbar inserts markers rather than styling a selection, so what is
              sent as `text/plain` is exactly what is on screen and the rich half
              is derived from it rather than the other way about. */}
          <div className="compose-toolbar" role="toolbar" aria-label="Formatting">
            {MARKERS.map((marker) => (
              <button
                key={marker.label}
                type="button"
                className="compose-marker"
                title={marker.title}
                onClick={() => setText((current) => applyMarker(bodyRef.current, current, marker))}
              >
                {marker.label}
              </button>
            ))}
            <button
              type="button"
              className="compose-marker compose-preview-toggle"
              aria-pressed={previewing}
              onClick={() => setPreviewing((on) => !on)}
            >
              Preview
            </button>
          </div>

          {previewing ? (
            // RENDERED WITH THE SAME FUNCTION THE ORIGIN WILL USE, so the
            // preview cannot disagree with what is sent. `dangerouslySetInnerHTML`
            // is safe here for the one reason that makes it ever safe: this
            // markup was generated from escaped text by a renderer that emits a
            // fixed set of tags, not parsed from anything.
            <div
              className="compose-preview"
              aria-label="Preview"
              dangerouslySetInnerHTML={{ __html: renderMarkup(text) }}
            />
          ) : (
            <textarea
              ref={bodyRef}
              className="compose-text"
              data-compose-body
              aria-label="Message"
              value={text}
              onChange={(event) => {
                edited.current = true
                setText(event.target.value)
              }}
            />
          )}
          {attachments.length > 0 && (
            <ul className="compose-attachments">
              {attachments.map((file) => (
                <li key={file.id}>
                  <span className="compose-attachment-name">{file.filename}</span>
                  <span className="compose-attachment-size">{Math.max(1, Math.round(file.size / 1000))} KB</span>
                  <button type="button" aria-label={`Remove ${file.filename}`} onClick={() => detach(file.id)}>
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="compose-actions">
          <label className="compose-attach">
            Attach…
            <input
              type="file"
              multiple
              aria-label="Attach files"
              onChange={(event) => {
                void attach(event.target.files)
                // Cleared so selecting the SAME file twice still fires a change.
                event.target.value = ''
              }}
            />
          </label>
          {uploading > 0 && <span className="compose-uploading">Attaching…</span>}
          <button type="button" className="compose-cancel" onClick={discard}>
            Discard
          </button>
          <button
            type="button"
            className="compose-send"
            onClick={() => void send()}
            // NOT WHILE A FILE IS STILL GOING UP. Sending then would build the
            // message without it, and the reader would have watched it upload.
            disabled={sending || uploading > 0}
          >
            {sending ? 'Queueing…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  )
}
